import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { Pagination } from "antd";
import CollapseHeader from "../../../core/common/collapse-header";
import { all_routes } from "../../router/all_routes";
import { getProducts, addProduct, updateProduct, deleteProduct, getProductDetail } from "./productAPI";
import ResponseModal from "../../../core/modals/response_modal";
interface Product {
  id: number;
  uuid: string;
  productName: string;
  productDescription: string;
  productCode: string;
  category: string;
  createdBy: string;
  createdDate: string;
  updatedBy: string;
  updatedDate: string;
}
interface ProductDetailData {
  id: number;
  uuid: string;
  name: string;
  description: string;
  code: string;
  category: string;
  createdBy: string;
  createdDate: string;
  updatedBy: string;
  updatedDate: string;
}

interface ProductForm {
  productName: string;
  productDescription: string;
  productCode: string;
  productCategory: string;
}

interface ProductListRequest {
  pageNo: number;
  pageSize: number;
  property?: string;
  direction?: "ASC" | "DESC";
  productName?: string;
  productCode?: string;
  category?: string;
}

const ProductList = () => {
  const route = all_routes;

  // State for product list
  const [data, setData] = useState<Product[]>([]);

  // State for product filtering
  const [filters, setFilters] = useState<ProductListRequest>({
    pageNo: 0,
    pageSize: 10,
    property: "id",
    direction: "ASC",
    productName: "",
    productCode: "",
    category: "",
  });

  // State for pagination
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalElements: 0,
  });

  // State for add form
  const [newProduct, setNewProduct] = useState<ProductForm>({
    productName: "",
    productDescription: "",
    productCode: "",
    productCategory: "",
  });

  // State for edit form
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductDetailData>();
  const [editedProductId, setEditedProductId] = useState<number | null>(null);
  const [response, setResponse] = useState<{ status: "success" | "error"; message: string } | null>(null);
  // State for delete confirmation modal
  const [productToDelete, setProductToDelete] = useState<{ id: number; name: string } | null>(null);

  // State for success modal when adding new product
  const [openModal, setOpenModal] = useState(false);
  const [createdProductId, setCreatedProductId] = useState<number | null>(null);

  // State for success modal when editing
  const [openEditSuccessModal, setOpenEditSuccessModal] = useState(false);

  // Function to handle filter input changes
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  // Function to handle sort order changes
  const handleSortChange = async (property: string, direction: "ASC" | "DESC") => {
    try {
      const newDirection = filters.direction === "ASC" ? "DESC" : "ASC";

      const updatedFilters = {
        ...filters,
        property,
        direction: newDirection as "ASC" | "DESC"
      };

      setFilters(updatedFilters);

      const response = await getProducts(updatedFilters);

      if (response.data && response.data.data) {
        setData(response.data.data.content || []);
        setPagination({
          totalPages: response.data.data.totalPages || 1,
          totalElements: response.data.data.totalElements || 0,
        });
      }
    } catch (error) {
      console.error("Error loading sorted product list:", error);
    }
  };

  // Function to apply filters
  const applyFilters = async () => {
    try {
      setFilters((prevFilters) => {
        const updatedFilters = { ...prevFilters, pageNo: 0 };
        console.log("Reset pageNo before sending filters:", updatedFilters);
        return updatedFilters;
      });

      const response = await getProducts({ ...filters, pageNo: 0 });

      console.log("Received response:", response.data);
      setData(response.data.data.content || []);
      setPagination({
        ...pagination,
        totalElements: response.data.data.totalElements,
        totalPages: response.data.data.totalPages
      });
    } catch (error) {
      console.error("Error getting product list with filters:", error);
    }
  };

  // Function to handle page changes
  const handlePageChange = (newPage: number) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      pageNo: newPage,
    }));
    fetchProducts(newPage);
  };

  // Function to fetch products list
  const fetchProducts = async (page: number = 0) => {
    try {
      const response = await getProducts({ ...filters, pageNo: page });
      console.log("API Response:", response.data);

      if (response.data && response.data.data && response.data.data.content) {
        // Đảm bảo dữ liệu được map đúng cách
        const products = response.data.data.content.map((product: any) => ({
          id: product.id,
          uuid: product.uuid,
          productName: product.productName,
          productDescription: product.productDescription || "",
          productCode: product.productCode,
          category: product.category || "",
          createdBy: product.createdBy || "",
          createdDate: product.createdDate || "",
          updatedBy: product.updatedBy || "",
          updatedDate: product.updatedDate || ""
        }));

        setData(products);
        setPagination({
          totalPages: response.data.data.totalPages || 1,
          totalElements: response.data.data.totalElements || 0,
        });
      } else {
        console.error("API response không hợp lệ:", response.data);
        setData([]);
      }
    } catch (error) {
      console.error("Lỗi khi lấy danh sách sản phẩm:", error);
      setData([]);
    }
  };

  // Function to handle input changes for add/edit forms
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Update state for add form
    setNewProduct(prevState => ({
      ...prevState,
      [name]: value
    }));

    // Update state for edit form
    setEditingProduct((prevState) => {
      if (!prevState) return prevState;
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // Function to create a new product
  const handleCreateProduct = async () => {
    try {
      // Kiểm tra các trường bắt buộc
      if (!newProduct.productName || !newProduct.productCode) {
        console.error("Thiếu thông tin bắt buộc: Tên sản phẩm hoặc Mã sản phẩm");
        alert("Vui lòng điền đầy đủ các trường bắt buộc: Tên sản phẩm, Mã sản phẩm");
        return;
      }

      // Chuẩn bị dữ liệu sản phẩm
      const productData = {
        productName: newProduct.productName.trim(),
        productDescription: newProduct.productDescription?.trim() || "",
        productCode: newProduct.productCode.trim(),
        productCategory: newProduct.productCategory?.trim() || "TEST" // Đặt giá trị mặc định là "TEST" nếu không có
      };

      console.log("Dữ liệu sản phẩm trước khi gửi lên API:", productData);

      const response = await addProduct(productData);
      console.log("API Response:", response.data);

      if (response.status === 200) {
        // Lấy ID của sản phẩm vừa tạo
        const createdId = response.data.data.id;
        console.log("Sản phẩm đã được tạo thành công với ID:", createdId);
        setCreatedProductId(createdId);
        setOpenModal(true);

        // Làm mới danh sách sản phẩm
        await fetchProducts(0);

        // Reset form
        setNewProduct({
          productName: "",
          productDescription: "",
          productCode: "",
          productCategory: "",
        });
      }
    } catch (error: any) {
      console.error("Lỗi khi tạo sản phẩm:", error);
      alert("Lỗi khi tạo sản phẩm: " + (error.response?.data?.message || error.message));
    }
  };

  // Function to handle edit button click
  const handleEditClick = async (productId: number) => {
    console.log("Product ID:", productId);
    try {
      const response = await getProductDetail(productId);
      console.log("API response:", response.data.data);

      if (response.status === 200) {
        setEditingProduct(response.data.data);
        setEditedProductId(response.data.data.id);
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
    }
  };

  // Function to update product
  const handleUpdateProduct = async () => {
    if (!editingProduct?.id) {
      console.error("Error: No valid ID found for update!", editingProduct);
      return;
    }

    try {
      console.log("Updating product with ID:", editingProduct.id);

      // Prepare update data
      const updatedProduct = {
        productName: editingProduct.name,
        productDescription: editingProduct.description || "",
        productCode: editingProduct.code,
        productCategory: editingProduct.category || ""
      };

      console.log("Sending product update data:", updatedProduct);
      const response = await updateProduct(editingProduct.id, updatedProduct);

      if (response.status === 200) {
        console.log("Product updated successfully");
        setEditedProductId(editingProduct.id);
        setOpenEditSuccessModal(true);

        // Refresh product list
        fetchProducts(0);
      }
    } catch (error: any) {
      console.error("Error updating product:", error);
      const errorMessage = error.response?.data?.message || error.message || "Unknown error";
      alert("Error updating product: " + errorMessage);
    }
  };

  // Function to handle delete button click
  const handleDeleteClick = (id: number, name: string) => {
    setProductToDelete({ id, name });
  };

  // Function to delete product
  const handleConfirmDelete = async () => {
    if (!productToDelete) return;
    try {
      const response = await deleteProduct(productToDelete.id);

      if (response.status === 200) {
        setData(prev => prev.filter(product => product.id !== productToDelete.id));
        setResponse({
          status: "success",
          message: `Product has been deleted successfully!`
        });
      } else {
        setResponse({
          status: "error",
          message: response.data?.message || "Failed to delete product"
        });
      }
    } catch (error: any) {
      console.error("Error deleting product:", error);
      setResponse({
        status: "error",
        message: error.response?.data?.message || "Error deleting product"
      });
    } finally {
      setShowMessageModal(true);
    }
  };

  // Fetch product list when component is loaded
  useEffect(() => {
    fetchProducts();
  }, []);

  // Define columns for table
  const columns = [
    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName",
      render: (text: string, record: Product) => (
        <Link to={`/crm/products-details/${record.id}`} className="title-name">
          {text}
        </Link>
      ),
      sorter: (a: Product, b: Product) => a.productName.localeCompare(b.productName),
    },
    {
      title: "Product Code",
      dataIndex: "productCode",
      key: "productCode",
      sorter: (a: Product, b: Product) => a.productCode.localeCompare(b.productCode),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      sorter: (a: Product, b: Product) => a.category.localeCompare(b.category),
    },
    {
      title: "Description",
      dataIndex: "productDescription",
      key: "productDescription",
      render: (text: string) => text || "-",
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: (a: Product, b: Product) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
    },
    {
      title: "Updated By",
      dataIndex: "updatedBy",
      key: "updatedBy",
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_: unknown, record: Product) => (
        <div className="dropdown table-action">
          <Link
            to="#"
            className="action-icon"
            data-bs-toggle="dropdown"
            aria-expanded="true"
          >
            <i className="fa fa-ellipsis-v"></i>
          </Link>
          <div
            className="dropdown-menu dropdown-menu-right"
            style={{
              position: "absolute",
              inset: "0px auto auto 0px",
              margin: "0px",
              transform: "translate3d(-99.3333px, 35.3333px, 0px)",
            }}
            data-popper-placement="bottom-start"
          >
            {/* Edit Action */}
            <Link
              className="dropdown-item edit-popup"
              to="#"
              data-bs-toggle="offcanvas"
              data-bs-target="#edit_Product"
              onClick={() => record.id && handleEditClick(record.id)}
            >
              <i className="ti ti-edit text-blue"></i> Edit
            </Link>

            {/* Delete Action */}
            <Link
              className="dropdown-item"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#delete_product"
              onClick={() => record.id && handleDeleteClick(record.id, record.productName)}
            >
              <i className="ti ti-trash text-danger"></i> Delete
            </Link>
          </div>
        </div>
      ),
    }
  ];

  // Add unique key for each data row
  const productsWithKeys = data.map((product, index) => ({
    ...product,
    key: product.id || index,
  }));

  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">
                      Products<span className="count-title">{pagination.totalElements}</span>
                    </h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
              <div className="card ">
                <div className="card-header">
                  {/* Search */}
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="icon-form mb-3 mb-sm-0">
                        <span className="form-icon">
                          <i className="ti ti-search" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search products"
                          name="productName"
                          value={filters.productName}
                          onChange={handleFilterChange}
                        />
                      </div>
                    </div>
                    <div className="col-sm-8">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        <div className="dropdown me-2">
                          <Link
                            to="#"
                            className="dropdown-toggle"
                            data-bs-toggle="dropdown"
                          >
                            <i className="ti ti-package-export me-2" />
                            Export
                          </Link>
                          <div className="dropdown-menu  dropdown-menu-end">
                            <ul>
                              <li>
                                <Link to="#" className="dropdown-item">
                                  <i className="ti ti-file-type-pdf text-danger me-1" />
                                  Export PDF
                                </Link>
                              </li>
                              <li>
                                <Link to="#" className="dropdown-item">
                                  <i className="ti ti-file-type-xls text-green me-1" />
                                  Export Excel
                                </Link>
                              </li>
                            </ul>
                          </div>
                        </div>
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_add"
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add Product
                        </Link>
                      </div>
                    </div>
                  </div>
                  {/* /Search */}
                </div>

                <div className="card-body">
                  {/* Filter */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <div className="dropdown me-2">
                        <Link
                          to="#"
                          className="dropdown-toggle"
                          data-bs-toggle="dropdown"
                        >
                          <i className="ti ti-sort-ascending-2 me-2" />
                          Sort
                        </Link>
                        <div className="dropdown-menu  dropdown-menu-start">
                          <ul>
                            <li>
                              <button className="dropdown-item" onClick={() => handleSortChange("productName", "ASC")}>
                                <i className="ti ti-circle-chevron-right me-1" />
                                Ascending
                              </button>
                            </li>
                            <li>
                              <button className="dropdown-item" onClick={() => handleSortChange("productName", "DESC")}>
                                <i className="ti ti-circle-chevron-right me-1" />
                                Descending
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <div className="form-sorts dropdown me-2">
                        <Link
                          to="#"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                        >
                          <i className="ti ti-filter-share" />
                          Filter
                        </Link>
                        <div className="filter-dropdown-menu dropdown-menu dropdown-menu-md-end p-3">
                          <div className="filter-set-view">
                            <div className="filter-set-head">
                              <h4>
                                <i className="ti ti-filter-share" />
                                Filter
                              </h4>
                            </div>
                            <div className="accordion" id="accordionExample">
                              <div className="filter-set-content">
                                <div className="filter-set-content-head">
                                  <Link
                                    to="#"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#collapseTwo"
                                  >
                                    Product Name
                                  </Link>
                                </div>
                                <div
                                  className="filter-set-contents accordion-collapse collapse show"
                                  id="collapseTwo"
                                >
                                  <div className="filter-content-list">
                                    <input
                                      type="text"
                                      className="form-control"
                                      name="productName"
                                      value={filters.productName}
                                      onChange={handleFilterChange}
                                      placeholder="Enter product name"
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="filter-reset-btns">
                                <div className="row">
                                  <div className="col-6">
                                    <button className="btn btn-light" onClick={() => setFilters((prevFilters) => ({
                                      ...prevFilters,
                                      productName: "",
                                    }))}>
                                      Reset
                                    </button>
                                  </div>
                                  <div className="col-6">
                                    <button className="btn btn-primary" onClick={applyFilters}>
                                      Apply
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="view-icons">
                        <Link to="#" className="active">
                          <i className="ti ti-list-tree" />
                        </Link>
                        <Link to="#">
                          <i className="ti ti-grid-dots" />
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* /Filter */}
                  {/* Product List */}
                  <div className="table-responsive custom-table">
                    <Table dataSource={productsWithKeys} columns={columns} />
                  </div>
                  {/* /Product List */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Add Product */}
      <div
        className="offcanvas offcanvas-end offcanvas-large"
        tabIndex={-1}
        id="offcanvas_add"
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Add New Product</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form>
            <div className="accordion" id="main_accordion">
              {/* Basic Info */}
              <div className="accordion-item rounded mb-3">
                <div className="accordion-header">
                  <Link
                    to="#"
                    className="accordion-button accordion-custom-button bg-white rounded fw-medium text-dark"
                    data-bs-toggle="collapse"
                    data-bs-target="#basic"
                  >
                    <span className="avatar avatar-md rounded text-dark border me-2">
                      <i className="ti ti-shopping-cart fs-20" />
                    </span>
                    Basic Information
                  </Link>
                </div>
                <div
                  className="accordion-collapse collapse show"
                  id="basic"
                  data-bs-parent="#main_accordion"
                >
                  <div className="accordion-body border-top">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Product Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="productName"
                            value={newProduct.productName}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Product Code <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="productCode"
                            value={newProduct.productCode}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="col-form-label">Category</label>
                          <input
                            type="text"
                            className="form-control"
                            name="productCategory"
                            value={newProduct.productCategory}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="mb-0">
                          <label className="col-form-label">Description</label>
                          <textarea
                            className="form-control"
                            rows={5}
                            name="productDescription"
                            value={newProduct.productDescription}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* /Basic Info */}
            </div>
            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                data-bs-dismiss="offcanvas"
                className="btn btn-light me-2"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateProduct}
              >
                Create
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* /Add Product */}

      {/* Edit Product */}
      <div
        className="offcanvas offcanvas-end offcanvas-large"
        tabIndex={-1}
        id="edit_Product"
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Edit Product</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form>
            <div className="accordion" id="main_accordion_2">
              {/* Basic Info */}
              <div className="accordion-item rounded mb-3">
                <div className="accordion-header">
                  <Link
                    to="#"
                    className="accordion-button accordion-custom-button bg-white rounded fw-medium text-dark"
                    data-bs-toggle="collapse"
                    data-bs-target="#basic_edit"
                  >
                    <span className="avatar avatar-md rounded text-dark border me-2">
                      <i className="ti ti-shopping-cart fs-20" />
                    </span>
                    Basic Information
                  </Link>
                </div>
                <div
                  className="accordion-collapse collapse show"
                  id="basic_edit"
                  data-bs-parent="#main_accordion_2"
                >
                  <div className="accordion-body border-top">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Product Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="name"
                            value={editingProduct?.name}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Product Code <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="code"
                            value={editingProduct?.code}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="col-form-label">Category</label>
                          <input
                            type="text"
                            className="form-control"
                            name="category"
                            value={editingProduct?.category}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="mb-0">
                          <label className="col-form-label">Description</label>
                          <textarea
                            className="form-control"
                            rows={5}
                            name="description"
                            value={editingProduct?.description}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* /Basic Info */}
            </div>
            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                data-bs-dismiss="offcanvas"
                className="btn btn-light me-2"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleUpdateProduct}
              >
                Update
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* /Edit Product */}

      {/* Delete Product */}
      <div className="modal fade" id="delete_product" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="text-center">
                <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                  <i className="ti ti-trash-x fs-36 text-danger" />
                </div>
                <h4 className="mb-2">Delete Product?</h4>
                <p className="mb-0">
                  Are you sure you want to delete this product?
                </p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <Link
                    to="#"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <button
                    data-bs-dismiss="modal"
                    className="btn btn-danger"
                    onClick={handleConfirmDelete}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Delete Product */}

      {/* Create Product Success Modal */}
      <Modal show={openModal} onHide={() => setOpenModal(false)}>
        <div className="modal-header border-0 m-0 justify-content-end">
          <button
            className="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={() => setOpenModal(false)}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="modal-body">
          <div className="success-message text-center">
            <div className="success-popup-icon bg-light-blue">
              <i className="ti ti-medal" />
            </div>
            <h3>Product has been created successfully!</h3>
            <p>View created product details</p>
            <div className="col-lg-12 text-center modal-btn">
              <Link to="#" className="btn btn-light" data-bs-dismiss="modal" onClick={() => setOpenModal(false)}>
                Cancel
              </Link>
              <Link
                to={`/crm/products-details/${createdProductId}`}
                onClick={() => setOpenModal(false)}
                className="btn btn-primary"
              >
                OK
              </Link>
            </div>
          </div>
        </div>
      </Modal>
      {/* /Create Product Success Modal */}

      {/* Edit Product Success Modal */}
      <Modal show={openEditSuccessModal} onHide={() => setOpenEditSuccessModal(false)}>
        <div className="modal-body">
          <div className="success-message text-center">
            <div className="success-popup-icon bg-light-blue">
              <i className="ti ti-chart-arcs" />
            </div>
            <h3>Product has been updated successfully!</h3>
            <div className="col-lg-12 text-center modal-btn">
              <Link
                to="#"
                onClick={() => {
                  setOpenEditSuccessModal(false);
                }}
                className="btn btn-primary"
              >
                OK
              </Link>
            </div>
          </div>
        </div>
      </Modal>
      {/* /Edit Product Success Modal */}
      <ResponseModal show={showMessageModal} onClose={() => setShowMessageModal(false)} response={response} />
    </div>
  );
};

export default ProductList; 